import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/TopNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { OwlLoader } from "@/components/OwlLoader";

const POLL_MS = 2000;
const MAX_POLLS = 15;

/** Landing page after Stripe Checkout. Polls until the deposit is confirmed paid. */
export default function PaymentReturn({ cancelled = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (cancelled) return undefined;
    if (!sessionId) { setState("error"); return undefined; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { setState("error"); return; }
      tries.current += 1;
      if (tries.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  const views = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment",
      body: "This takes a few seconds — please don't close this page." },
    paid: { icon: CheckCircle2, tone: "text-emerald-600", title: "Deposit complete",
      body: info ? `$${Number(info.credited_usd).toLocaleString()} is in your USD wallet.` : "Your wallet has been credited." },
    cancelled: { icon: XCircle, tone: "text-muted-foreground", title: "Payment cancelled",
      body: "Nothing was charged. You can start again whenever you like." },
    failed: { icon: XCircle, tone: "text-red-600", title: "Payment didn't go through",
      body: "Your card wasn't charged. Try again or use a different card." },
    timeout: { icon: Clock, tone: "text-amber-600", title: "Still processing",
      body: "Your bank is taking longer than usual. We'll credit your wallet as soon as it clears." },
    error: { icon: XCircle, tone: "text-red-600", title: "We couldn't check that payment",
      body: "Head to your wallet — if the money left your card it will appear there shortly." },
  };
  const v = views[state] || views.error;
  const Icon = v.icon;

  return (
    <AppShell>
      <div className="max-w-lg mx-auto mt-6">
        <Card className="p-7 shadow-premium text-center" data-testid="payment-return-card">
          {v.spin
            ? <OwlLoader size="lg" className="justify-center" />
            : <Icon className={`h-10 w-10 mx-auto ${v.tone || "text-muted-foreground"}`} />}
          <h1 className="mt-3 text-xl font-semibold" data-testid="payment-return-title">{v.title}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{v.body}</p>
          {info?.amount && (
            <p className="mt-2 text-xs text-muted-foreground numeric" data-testid="payment-return-charged">
              Charged {Number(info.amount).toLocaleString()} {info.currency}
            </p>
          )}
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="secondary" onClick={() => navigate("/wallet")} data-testid="payment-return-wallet">
              Go to wallet
            </Button>
            <Button onClick={() => navigate("/")} data-testid="payment-return-home">Dashboard</Button>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
